import React, { useEffect, useRef, useState } from "react";
import GrayCard from "components/gray-card";
import Radio from "components/radio";
import styled from "styled-components";
import Typography from "components/typography";
import { Colors } from "utils/colors";
import { FormatAudioDuration } from "utils/dateConvert";
import ToggleAudio from "components/toggleAudio";

interface IProps {
  audio: File;
  previewData: { startTime: number; endTime: number };
  setPreviewData: (data?: { startTime: number; endTime: number }) => void;
}
const PreviewClip = ({ audio, previewData, setPreviewData }: IProps) => {
  const [previewClip, setPreviewClip] = useState(false);

  const [range1, setRange1] = useState(0);
  const [range2, setRange2] = useState(10);

  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handleLoadedMetadata = (event: any) => {
    const audioDuration = event.target.duration;
    setDuration(audioDuration);
  };

  const handleTimeUpdate = (event: any) => {
    if (audioRef.current) {
      const current = event.currentTarget.currentTime;
      if (current >= (range2 / 100) * duration) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const TogglePlay = () => {
    if (audioRef.current && !isPlaying) {
      audioRef.current.currentTime = (range1 / 100) * duration;
      audioRef.current.play();
      setIsPlaying(true);
    }
    if (audioRef.current && isPlaying) {
      setIsPlaying(false);
      audioRef.current.pause();
    }
  };

  // Define the max distance between the sliders
  const maxDistance = 3300 / duration; // 30 seconds

  const handleSlideOne = (e: any) => {
    const newValue = Number(e.target.value);

    if (range2 - newValue <= maxDistance && newValue < range2) {
      const time = Math.floor((newValue / 100) * duration);
      setRange1(newValue);
      setPreviewData({
        ...previewData,
        startTime: time
      });
    }
  };

  const handleSlideTwo = (e: any) => {
    const newValue = Number(e.target.value);

    if (newValue - range1 <= maxDistance && newValue > range1) {
      const time = Math.floor((newValue / 100) * duration);
      setRange2(newValue);
      setPreviewData({
        ...previewData,
        endTime: time
      });
    }
  };

  return (
    <GrayCard title="Preview Clip Start Time (TikTok, Apple Music, iTunes)">
      <audio
        controls
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        style={{ display: "none" }}
        ref={audioRef}
      >
        <source src={URL.createObjectURL(audio)} type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
      <RadioItem>
        <Radio value={!previewClip} onClick={() => setPreviewClip(false)} />
        <Typography color={Colors.grey100} ml="0.4">
          Let streaming services decide
        </Typography>
      </RadioItem>
      <RadioItem>
        <Radio value={previewClip} onClick={() => setPreviewClip(true)} />
        <Typography color={Colors.grey100} ml="0.4">
          Let me specify when the good part starts
        </Typography>
      </RadioItem>
      {previewClip && (
        <>
          <AudioBarContainer>
            <ToggleAudio toggleAudio={TogglePlay} />
            <AudioContainer>
              <AbsoulteText>
                <Typography size="s" color={Colors.yellow100}>
                  {FormatAudioDuration((range1 / 100) * duration)} -
                </Typography>
                <Typography size="s" color={Colors.yellow100} ml="0.2">
                  {FormatAudioDuration((range2 / 100) * duration)}
                </Typography>
              </AbsoulteText>
              <SliderTrack minvalue={range1} maxvalue={range2} />
              <SliderElement>
                <RangeInput
                  min="0"
                  max="100"
                  value={range1}
                  id="slider-1"
                  onInput={handleSlideOne}
                  step={1}
                />
              </SliderElement>

              <RangeInput
                min="0"
                max="100"
                value={range2}
                id="slider-2"
                onInput={handleSlideTwo}
                step={1}
              />
            </AudioContainer>
          </AudioBarContainer>
          <InfoContainer>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M11 6C11 8.7614 8.7614 11 6 11C3.23857 11 1 8.7614 1 6C1 3.23857 3.23857 1 6 1C8.7614 1 11 3.23857 11 6ZM6 8.875C6.2071 8.875 6.375 8.7071 6.375 8.5V5.5C6.375 5.2929 6.2071 5.125 6 5.125C5.7929 5.125 5.625 5.2929 5.625 5.5V8.5C5.625 8.7071 5.7929 8.875 6 8.875ZM6 3.5C6.27615 3.5 6.5 3.72386 6.5 4C6.5 4.27614 6.27615 4.5 6 4.5C5.72385 4.5 5.5 4.27614 5.5 4C5.5 3.72386 5.72385 3.5 6 3.5Z"
                fill="#909090"
              />
            </svg>
            <Typography size="s" color={Colors.grey500} ml="0.4">
              Drag the preview bar to select your track preview start time. The
              30 second track preview you select will be featured on your Rated
              fans link and platforms such as TikTok
            </Typography>
          </InfoContainer>
        </>
      )}
    </GrayCard>
  );
};

export default PreviewClip;

const RadioItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2.4rem;
`;

const AudioBarContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2.4rem;
`;

const AudioContainer = styled.div`
  position: relative;
  width: 80%;
  height: 2.8rem;
  margin-left: 1rem;
`;

const SliderElement = styled.div`
  position: relative;
  height: 2.8rem;
`;

const AbsoulteText = styled.div`
  position: absolute;
  top: -1.8rem;
  left: 0;
  display: flex;
  align-items: center;
`;

const SliderTrack = styled.div<{ minvalue: number; maxvalue: number }>`
  width: 100%;
  height: 100%;
  position: absolute;
  margin: auto;
  top: 0;
  bottom: 0;
  background-color: rgba(254, 198, 103, 0.1);
  background: ${(props) => `linear-gradient(to right, 
    rgba(254, 198, 103, 0.10) ${props.minvalue}%,
    ${Colors.yellow500} ${props.minvalue}%,
    ${Colors.yellow500} ${props.maxvalue}%,
    rgba(254, 198, 103, 0.10) ${props.maxvalue}%)`};
`;

const RangeInput = styled.input.attrs({ type: "range" })`
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 100%;
  outline: none;
  position: absolute;
  margin: auto;
  top: 0;
  bottom: 0;
  background-color: transparent;
  pointer-events: none;

  &::-webkit-slider-runnable-track {
    -webkit-appearance: none;
    height: 5px;
  }

  &::-moz-range-track {
    -moz-appearance: none;
    height: 5px;
  }

  &::-ms-track {
    appearance: none;
    height: 5px;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    height: 2.8rem;
    width: 1px;
    background-color: ${Colors.yellow500};
    cursor: pointer;
    margin-top: -9px;
    pointer-events: auto;
  }

  &::-moz-range-thumb {
    -webkit-appearance: none;
    height: 1.7rem;
    width: 1.7rem;
    cursor: pointer;
    background-color: ${Colors.yellow500};
    pointer-events: auto;
    border: none;
  }

  &::-ms-thumb {
    appearance: none;
    height: 1.7em;
    width: 1.7em;
    cursor: pointer;

    background-color: ${Colors.yellow500};
    pointer-events: auto;
  }

  &:active::-webkit-slider-thumb {
    background-color: #ffffff;
    border: 1px solid ${Colors.yellow500};
  }
`;

const InfoContainer = styled.div`
  display: flex;
  align-items: center;
`;
